import React, { ReactNode, useState, useContext, useRef, useEffect } from 'react';
import { AppContext } from '../app.tsx';
import { Configuration } from '../../data/configuration.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SettingsOptionsProps {
	trigger: (openModal: () => void) => ReactNode;
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

export default function SettingsOptions(props: SettingsOptionsProps) {

	// get the app context and ensure it is valid.
	const context = useContext(AppContext);
	if (!context) {
		throw new Error('AppContext must be used within the main App component');
	}

	//refs.
	const triggerRef = useRef<HTMLDivElement>(null);
	const tooltipRef = useRef<HTMLDivElement>(null);

	// state.
	const [isOpen, setIsOpen] = useState(!context.isMobile);
	const [tooltipStyle, setTooltipStyle] = useState({});
	const [windowsUp, setWindowsUp] = useState(false);
	const [licensePlateVisible, setLicensePlateVisible] = useState(true);

	// handlers.

	const openTooltip = () => setIsOpen(!isOpen);
	const closeTooltip = () => context.isMobile && setIsOpen(false);

	const toggleLicensePlate = () => {
		context.exteriorConfigurator.selectFrontLicensePlateState(!licensePlateVisible);
		setLicensePlateVisible(!licensePlateVisible);
	}

	const toggleWindows = () => {
		context.exteriorConfigurator.selectSideWindowState(!windowsUp);
		setWindowsUp(!windowsUp);
	}

	const toggleHood = () => {
		context.exteriorConfigurator.selectSoftTopState(!context.hoodUp);
		context.setHoodUp(!context.hoodUp);
	}

	useEffect(() => {

		// close tooltip if clicked outside of it.
		const handleClickOutside = (event: globalThis.MouseEvent | TouchEvent) => {
			const target = event.target as Node | null;
			if (!target) return;

			const clickedOutsideTooltip =
				tooltipRef.current && !tooltipRef.current.contains(target);
			const clickedOutsideTrigger =
				triggerRef.current && !triggerRef.current.contains(target);

			if (clickedOutsideTooltip && clickedOutsideTrigger) {
				closeTooltip();
			}
		};
		
		if (isOpen && context.isMobile) {

			// listen for clicks outside the tooltip to close it.
			document.addEventListener('mousedown', handleClickOutside);
			document.addEventListener('touchstart', handleClickOutside);

			// calculate tooltip position based on dimensions
			if (triggerRef.current && tooltipRef.current) {
				const rect = triggerRef.current.getBoundingClientRect();
				setTooltipStyle({
					position: 'absolute',
					top: rect.top + rect.height,
					left: 4,
				});
			}
		} else {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);

		}

		// cleanup listener on unmount.
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
			document.removeEventListener('touchstart', handleClickOutside);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOpen, context.isMobile]);


	// return the HTML.
	return (
		<React.Fragment>
			<div ref={triggerRef} id="settings-trigger">
				{props.trigger && props.trigger(openTooltip)}
			</div>

			{isOpen && (
				<div ref={tooltipRef} style={tooltipStyle}  id="settings-options-container" onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}>
					<div id="settings-tooltip-body">
						{Configuration.isUsRegion() && (
							<div role="button" onClick={toggleLicensePlate} style={{ opacity: licensePlateVisible ? 1 : 0.6 }}>
								<img src={'assets/images/icons/plus-four-us-licence-icon.svg'} width={70} height="auto" />
							</div>
						)}
							
						<div role="button" onClick={toggleWindows} style={{ opacity: windowsUp ? 1 : 0.6 }}>
							<img src={'assets/images/icons/sidescreen-toggle.svg'} width={70} height="auto"  />
						</div>
						<div role="button" onClick={toggleHood} style={{ opacity: context.hoodUp ? 1 : 0.6 }}>
							<img src={'assets/images/icons/soft-top-toggle.svg'} width={70} height="auto" />
						</div>
					</div>
				</div>
			)}
		</React.Fragment>
	);
}
